import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { SegmentNumberOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoHistoricalOrderValueSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import {
  RedoCurrencyInput,
  RedoCurrencyInputValue,
} from "@redotech/redo-web/arbiter-components/input/redo-currency-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const HistoricalOrderValueInspectorContent = memo(
  function HistoricalOrderValueInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoHistoricalOrderValueSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("Customer's historical order value is")}{" "}
          {segmentNumberOperatorToText()[segment.operator]}{" "}
          {moneyFormat({ amount: segment.amount, currency: segment.currency })}
        </Text>
      </Flex>
    );
  },
);

export const EditableHistoricalOrderValueInspectorContent = memo(
  function EditableHistoricalOrderValueInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoHistoricalOrderValueSegmentSchema.parse(step.segment),
      [step],
    );

    const segmentValue = useMemo(
      () => ({ amount: segment.amount, currency: segment.currency }),
      [segment.amount, segment.currency],
    );

    const handleOperatorChange = useHandler(
      (operator: SegmentNumberOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleValueChange = useHandler((value: RedoCurrencyInputValue) => {
      const amount = value.amount ?? 0;
      setStep({
        ...step,
        segment: { ...segment, amount, currency: value.currency },
      });
    });

    const selectedOperator = operatorOptions().find(
      (o) => o.value === segment.operator,
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          <Flex align="center" dir="row" gap="md" wrap="wrap">
            <Text fontSize="xs" fontWeight="medium">
              {t("Historical order value is")}
            </Text>
            <RedoSingleSelectDropdownInput<SegmentNumberOperator>
              fitToAnchor={false}
              flexGrow={false}
              options={operatorOptions()}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              placeholder={t("Select comparison")}
              selectedItem={selectedOperator}
              size="xs"
            />
          </Flex>
          <Flex align="center" dir="row" gap="sm">
            <RedoCurrencyInput
              placeholder={0}
              setValue={handleValueChange}
              size="sm"
              value={segmentValue}
            />
          </Flex>
        </Flex>
      </>
    );
  },
);

function operatorOptions(): TextRedoListItem<SegmentNumberOperator>[] {
  return [
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN,
      value: SegmentNumberOperator.LESS_THAN,
      text: t("%s"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      value: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      text: t("%s"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.EQUAL,
      value: SegmentNumberOperator.EQUAL,
      text: t("%s"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN,
      value: SegmentNumberOperator.GREATER_THAN,
      text: t("%s"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      value: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      text: t("greater than or equal to"),
    },
  ];
}

function segmentNumberOperatorToText(): Record<SegmentNumberOperator, string> {
  return {
    [SegmentNumberOperator.LESS_THAN]: t("less than"),
    [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: t("less than or equal to"),
    [SegmentNumberOperator.EQUAL]: t("equal to"),
    [SegmentNumberOperator.GREATER_THAN]: t("greater than"),
    [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: t(
      "greater than or equal to",
    ),
  };
}
